import React, { useEffect, useState } from "react";
import heroImg from "../assets/hero_banner.jpg";

// Pulled from Anfield's own copy — no invented claims.
const STATS = [
  { value: "1989", label: "Established" },
  { value: "100+", label: "Yrs — owners’ combined experience" },
  { value: "42″", label: "Max casing fished" },
  { value: "QHSEMS", label: "Safety management system" },
];

const HEADLINE = ["Wellbore", "Intervention", "Specialists"];

export default function HeroBanner() {
  const [mounted, setMounted] = useState(false);

  // Kick the entrance animation on the first paint after mount
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink-950"
    >
      {/* Photograph, slowly pushing in */}
      <div className="absolute inset-0">
        <img
          src={heroImg}
          alt="Drilling rig silhouetted against a sunset"
          className="h-full w-full animate-drift object-cover object-center will-change-transform"
          fetchpriority="high"
        />
      </div>

      {/* Scrims: darken the base and the left edge so type stays legible */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-ink-950/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950/90 via-ink-950/30 to-transparent" />
      {/* Warm bloom behind the rig, echoing the sunset */}
      <div className="absolute left-1/2 top-1/4 h-[38rem] w-[38rem] -translate-x-1/2 animate-sheen rounded-full bg-flame-500/20 blur-[120px]" />
      {/* Engineering grid */}
      <div className="absolute inset-0 bg-grid bg-grid opacity-[0.55]" />

      <div className="relative mx-auto w-full max-w-shell px-6 pb-14 pt-40 lg:px-10 lg:pb-20">
        {/* Kicker */}
        <div
          className="mb-8 flex items-center gap-4 transition-all duration-700"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? "none" : "translateY(16px)",
          }}
        >
          <span className="h-10 w-1 bg-flame-500" />
          <span className="font-mono text-[11px] uppercase tracking-kicker text-flame-400">
            Est. 1989 · South Oropouche, Trinidad
          </span>
        </div>

        {/* Masked headline — each line wipes up in sequence */}
        <h1 className="h-display max-w-5xl text-[3.25rem] leading-[0.9] sm:text-7xl lg:text-[7.5rem]">
          {HEADLINE.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <span
                className="block"
                style={{
                  transform: mounted ? "none" : "translateY(105%)",
                  transition: `transform 1s cubic-bezier(.16,1,.3,1) ${
                    150 + i * 130
                  }ms`,
                  color: i === 2 ? "#F26522" : undefined,
                }}
              >
                {line}
              </span>
            </span>
          ))}
        </h1>

        {/* Sub-copy + actions */}
        <div
          className="mt-8 flex flex-col gap-10 transition-all duration-700 delay-[650ms] lg:flex-row lg:items-end lg:justify-between"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? "none" : "translateY(20px)",
          }}
        >
          <p className="max-w-xl text-base text-steel-300 sm:text-lg">
            Specialized provider of technical downhole fishing services for the
            drilling and workover operations of the oil and gas industry.
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => scrollTo("capabilities")}
              className="btn-flame group"
            >
              Our Capabilities
              <span className="transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </button>
            <button
              type="button"
              onClick={() => scrollTo("contact")}
              className="btn-ghost"
            >
              Contact Us
            </button>
          </div>
        </div>

        {/* Stat rail */}
        <dl
          className="mt-14 grid grid-cols-2 border-t border-white/15 transition-all duration-700 delay-[800ms] lg:grid-cols-4"
          style={{ opacity: mounted ? 1 : 0 }}
        >
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="border-b border-white/10 px-1 py-5 lg:border-b-0 lg:border-r lg:border-white/10 lg:px-6 lg:first:pl-0 lg:last:border-r-0"
            >
              <dt className="h-display text-3xl text-white lg:text-4xl">
                {stat.value}
              </dt>
              <dd className="mt-1 font-mono text-[10px] uppercase tracking-wide2 text-steel-400">
                {stat.label}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Scroll cue */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 hidden -translate-x-1/2 lg:block">
        <div className="flex h-10 w-6 justify-center rounded-full border border-white/25 pt-2">
          <span className="h-2 w-[2px] animate-scroll-cue rounded bg-flame-500" />
        </div>
      </div>
    </section>
  );
}
